import { Box, FormControl, FormControlLabel } from '@mui/material';
import {
  NestedFieldsSelector,
  NestedFieldsField,
} from '../../common/fieldsSelectors/NestedFieldsSelector';
import { useCallback } from 'react';
import { isEqual } from 'lodash';
import FieldLabel from '../../common/FieldLabel';
import { useSelector } from 'react-redux';
import {
  selectSlackIntegrationStatus,
  SlackConnectionStatus,
} from '../../../reducers/integrationsReducer';
import { Button, Checkbox, Typography, colors } from '@sweep-io/sweep-design';
import { SlackNotConnected } from './SlackNotConnected';
import { SlackRecipientsSelect } from './SlackRecipientsSelect';
import { useSlackRecipients } from './useSlackRecipients';
import { SlackNewMessageEditorLexical as SlackNewMessageEditor } from './SlackNewMessageEditorLexical';
import { useGetSlackRecipientsLabels } from './useGetSlackRecipientsLabels';
import { useSweepFieldsInfo } from '../../../sweep-fields/useSweepFieldsInfo';
import { SlackConstant } from './utils';
import { SlackAdditionalFieldsTable } from './SlackAdditionalFieldsTable';
import { SfdcDisplayType } from '../../../types/fields';

interface SlackDealRoomActionProps {
  readonly?: boolean;
  action: SlackDealRoomAutomationAction;
  crmOrgId: string;
  onChange: (slackDealRoomAutomationAction: SlackDealRoomAutomationAction) => any;
  objectName: string;
}

export const SlackDealRoomAction = ({
  readonly,
  action,
  crmOrgId,
  objectName,
  onChange,
}: SlackDealRoomActionProps) => {
  const slackStatus = useSelector(selectSlackIntegrationStatus);
  const { recipientWithLabelsArray } = useGetSlackRecipientsLabels({
    recipients: action.actionParams?.members ?? [],
  });
  const { getEnrichedNestedPath } = useSweepFieldsInfo();
  readonly = readonly || slackStatus !== SlackConnectionStatus.Connected;

  const updateOnChange = useCallback(
    (updateProps: Partial<AutomationSlackDealRoomActionParams>) => {
      onChange({
        ...action,
        actionParams: {
          ...action.actionParams,
          ...updateProps,
        },
      });
    },
    [action, onChange],
  );

  const setBeforeCallingFields = useCallback(() => {
    updateOnChange({
      fields: [...(action.actionParams?.fields || [])],
      channel: {
        ...action.actionParams?.channel,
        type: action.actionParams?.channel?.type ?? 'Public',
      },
    });
  }, [action.actionParams?.channel, action.actionParams?.fields, updateOnChange]);

  const { isLoading } = useSlackRecipients({
    objectName,
    crmOrgId,
    setBeforeCallingFields,
  });

  const handleDeleteChip = (chipToRemove: SlackRecipientStruct) => {
    const newRecipients = action.actionParams.members?.filter((item) => {
      if (chipToRemove.type === SlackConstant.Slack) {
        return item.value !== chipToRemove.value;
      } else {
        return !isEqual(
          (item.value as SlackFieldsStruct).fieldIds,
          (chipToRemove.value as SlackFieldsStruct).fieldIds,
        );
      }
    });
    updateOnChange({ members: newRecipients });
  };

  return (
    <>
      {slackStatus !== SlackConnectionStatus.Connected && (
        <Box sx={{ marginTop: '32px' }}>
          <SlackNotConnected slackStatus={slackStatus} />
        </Box>
      )}

      {(slackStatus === SlackConnectionStatus.Connected || action?.actionParams?.channel?.name) && (
        <Box
          sx={{
            background: colors.grey[100],
            padding: '24px 24px 19px',
            borderRadius: '8px',
            margin: '16px 0',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <SlackNewMessageEditor
            placeholder="Set channel name. E.g., dealroom-{opportunity.name}"
            customOnKeyDown={(event) => {
              if (!(event.key.match(/[a-z0-9,]/) || event.key === '-' || event.key === '_')) {
                event.preventDefault();
              }
            }}
            infoTooltipTitle={
              'Channel names may only contain lowercase letters, numbers, hyphens, and underscores, and must be 80 characters or less.'
            }
            hideEmoji={true}
            isRequired={true}
            label="Slack channel name"
            readonly={readonly}
            messageStruct={action.actionParams?.channel?.name}
            crmOrgId={crmOrgId}
            onChange={(newMsg) => {
              updateOnChange({
                channel: {
                  ...action.actionParams?.channel,
                  name: newMsg,
                },
              });
            }}
            objectName={objectName}
          />

          <Box sx={{ mb: '20px', mt: '-15px' }}>
            <FormControlLabel
              label={<Typography variant="body">Make private</Typography>}
              control={
                <Checkbox
                  disabled={readonly}
                  checked={action.actionParams?.channel?.type === 'Private'}
                  onChange={(value) => {
                    const isChecked = value.target.checked;
                    updateOnChange({
                      channel: {
                        ...action.actionParams?.channel,
                        type: isChecked ? 'Private' : 'Public',
                      },
                    });
                  }}
                />
              }
            />
          </Box>

          <FormControl
            sx={{
              width: '100%',
              marginBottom: '20px',
              maxWidth: '1148px',
            }}
          >
            <Box
              sx={{
                marginBottom: '3px',
                '& div': {
                  color: colors.grey[800],
                  fontSize: '12px',
                  fontWeight: '400',
                },
              }}
            >
              <FieldLabel label={'Slack channel members'} markLabelAsRequired={true} />
            </Box>

            <SlackRecipientsSelect
              customButtonText={'Add from Salesforce field'}
              objectName={objectName}
              crmOrgId={crmOrgId}
              isLoading={isLoading}
              onChange={(event, newValues) => {
                const newItems = newValues.map((el: any) => {
                  if (el.type === SlackConstant.Salesforce) {
                    return {
                      ...el,
                      type: SlackConstant.Salesforce,
                      value: {
                        fieldIds: el.value.fieldIds,
                      },
                    };
                  } else if (el.type === SlackConstant.Salesforce_Created_Slack_Channel) {
                    return {
                      ...el,
                      type: SlackConstant.Salesforce_Created_Slack_Channel,
                      value: {
                        fieldIds: el.value.fieldIds,
                      },
                    };
                  } else {
                    return {
                      ...el,
                      type: SlackConstant.Slack,
                      value: el.value,
                    };
                  }
                });
                updateOnChange({ members: newItems as SlackRecipientStruct[] });
              }}
              hideDisclaimer={true}
              readonly={!!readonly}
              recipientsNoChannels
              recipients={recipientWithLabelsArray}
              handleDeleteChip={handleDeleteChip}
            />
          </FormControl>

          <SlackNewMessageEditor
            placeholder="Type message body..."
            isRequired={false}
            label="Channel welcome message"
            readonly={readonly}
            messageStruct={action.actionParams?.message ?? { template: '', variables: [] }}
            crmOrgId={crmOrgId}
            onChange={(newMsg) => {
              updateOnChange({ message: newMsg });
            }}
            objectName={objectName}
          />

          <SlackAdditionalFieldsTable
            readonly={readonly}
            fields={action.actionParams?.fields}
            updateOnChange={(fields) => {
              updateOnChange({ fields });
            }}
            crmOrgId={crmOrgId}
            objectName={objectName}
          />

          <Box>
            <Box sx={{ marginBottom: '3px' }}>
              <FieldLabel label={'Save Slack channel ID on the triggering record (Optional)'} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box
                sx={{
                  border: `1px solid ${colors.grey[300]}`,
                  borderRadius: '4px',
                  background: colors.white,
                  maxWidth: '500px',
                  width: 'fit-content',
                }}
              >
                <NestedFieldsSelector
                  filterBy={(field: SweepField) => field?.sfDisplayType === SfdcDisplayType.STRING}
                  disableLookupItemsResolve
                  readonly={readonly}
                  crmOrgId={crmOrgId}
                  objectType={objectName}
                  placeholder="Choose field"
                  nestedPath={getEnrichedNestedPath(action?.actionParams?.channelIdField?.fieldIds)}
                  onChange={(sweepField: NestedFieldsField) => {
                    const newItem = {
                      fieldIds: sweepField.fieldIds,
                    };
                    updateOnChange({ channelIdField: newItem });
                  }}
                />
              </Box>
              {action?.actionParams?.channelIdField && (
                <Button
                  size="medium"
                  onClick={() => {
                    updateOnChange({ channelIdField: undefined });
                  }}
                  variant="flat-blue"
                >
                  Clear
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};
